import React, {useState, useEffect, useCallback, useMemo} from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { motion } from 'framer-motion';
import QuizLayout from '../../../../shared/reusables/QuizLayout';
import WordListLayout from '../../../../shared/reusables/WordListLayout';
import QuizCard from '../../../../shared/reusables/QuizCard';
import QuizEndScreen from '../../../../shared/reusables/QuizEndScreen';
import { fetchWordsByCategory } from '../../../../shared/api/darija';

const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * NounsMode — quiz mode for nouns with a category picker.
 * Kept separate from GenericQuizMode because it has a category-selection step.
 */
export default function NounsMode({ onBack }) {
    const [nounsData, setNounsData] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [wordOrder, setWordOrder] = useState([]);
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [input, setInput] = useState('');
    const [showAnswer, setShowAnswer] = useState(false);
    const [progress, setProgress] = useState([]);
    const [quizFinished, setQuizFinished] = useState(false);
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWordsByCategory('nouns')
            .then(setNounsData)
            .catch((err) => {
                console.error('[NounsMode]:', err);
                setError(err.message || 'Ophalen mislukt.');
            });
    }, []);

    useEffect(() => {
        if (!nounsData || !selectedCategory) return;
        const words =
            selectedCategory === 'willekeurig'
                ? Object.values(nounsData).flat()
                : nounsData[selectedCategory];
        const shuffled = shuffleArray(words.map((_, i) => i));
        setWordOrder(shuffled);
        setProgress(shuffled.map(() => ({ correct: false, revealed: false })));
        setCurrentWordIndex(0);
        setInput('');
        setShowAnswer(false);
        setQuizFinished(false);
    }, [nounsData, selectedCategory]);

    const addMessage = useCallback(
        (type, text) => setMessages((prev) => [...prev, { type, text, id: Date.now() }]),
        []
    );
    const handleCloseMessage = useCallback(
        (id) => setMessages((prev) => prev.filter((m) => m.id !== id)),
        []
    );

    const words = useMemo(
        () =>
            !nounsData || !selectedCategory
                ? []
                : selectedCategory === 'willekeurig'
                ? Object.values(nounsData).flat()
                : nounsData[selectedCategory] ?? [],
        [nounsData, selectedCategory]
    );

    const nextWord = useCallback(() => {
        if (currentWordIndex + 1 < wordOrder.length) {
            setCurrentWordIndex((i) => i + 1);
            setInput('');
            setShowAnswer(false);
        } else {
            setQuizFinished(true);
        }
    }, [currentWordIndex, wordOrder.length]);

    const handleSubmit = useCallback(() => {
        const word = words[wordOrder[currentWordIndex]];
        if (!word) return;

        if (progress[currentWordIndex]?.revealed) {
            addMessage('error', '❌ Antwoord laten zien telt als fout.');
            setTimeout(nextWord, 400);
            return;
        }

        const correct = input.trim().toLowerCase() === word.darija?.toLowerCase();

        if (correct) {
            addMessage('success', '✅ Goed gedaan!');
            setProgress((prev) => {
                const next = [...prev];
                next[currentWordIndex] = { ...next[currentWordIndex], correct: true };
                return next;
            });
            setTimeout(nextWord, 400);
        } else {
            addMessage('error', '❌ Fout! Probeer opnieuw.');
        }
    }, [words, wordOrder, currentWordIndex, progress, input, addMessage, nextWord]);

    const handleShowAnswer = useCallback(() => {
        setShowAnswer(true);
        setProgress((prev) => {
            const next = [...prev];
            next[currentWordIndex] = { correct: false, revealed: true };
            return next;
        });
    }, [currentWordIndex]);

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif" }}>
                    ⚠️ {error}
                </Typography>
            </Box>
        );
    }

    if (!nounsData) return null;

    const categories = Object.keys(nounsData);

    // ── Category picker ──────────────────────────────────────────────────────
    if (!selectedCategory) {
        return (
            <WordListLayout
                title="Kies een categorie"
                subtitle={`Zelfstandige naamwoorden · ${categories.length} categorieën`}
                accentColor="#14b8b8"
                accentColor2="#0e8c8c"
                label="Zelfstandige naamwoorden"
                onBack={onBack}
            >
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Button
                        fullWidth
                        onClick={() => setSelectedCategory('willekeurig')}
                        sx={{
                            mb: 3,
                            borderRadius: '16px',
                            py: 2.2,
                            px: 4,
                            textTransform: 'none',
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 700,
                            fontSize: '1rem',
                            background: 'rgba(14,140,140,0.15)',
                            border: '1px solid rgba(14,140,140,0.4)',
                            color: '#14b8b8',
                            justifyContent: 'flex-start',
                            gap: 1.5,
                            '&:hover': {
                                background: 'rgba(14,140,140,0.25)',
                                borderColor: 'rgba(14,140,140,0.7)',
                                color: '#5ee0e0',
                            },
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>🎲</span> Willekeurig — alle categorieën door elkaar
                    </Button>
                </motion.div>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
                        gap: 2,
                    }}
                >
                    {categories.map((cat, i) => (
                        <motion.div
                            key={cat}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.04, duration: 0.28 }}
                        >
                            <Button
                                fullWidth
                                onClick={() => setSelectedCategory(cat)}
                                sx={{
                                    height: { xs: 64, sm: 80 },
                                    borderRadius: '14px',
                                    textTransform: 'none',
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.09)',
                                    color: '#e8e8f0',
                                    transition: 'all 0.18s ease',
                                    '&:hover': {
                                        background: 'rgba(14,140,140,0.12)',
                                        borderColor: 'rgba(14,140,140,0.4)',
                                        color: '#5ee0e0',
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                {cat}
                            </Button>
                        </motion.div>
                    ))}
                </Box>
            </WordListLayout>
        );
    }

    // ── Quiz finished ────────────────────────────────────────────────────────
    if (quizFinished) {
        const correctCount = progress.filter((p) => p.correct).length;
        const wrongItems = progress
            .map((p, i) => ({ ...p, word: words[wordOrder[i]] }))
            .filter((p) => !p.correct)
            .map((w) => ({ question: w.word?.dutch, answer: w.word?.darija }));

        return (
            <QuizEndScreen
                correct={correctCount}
                total={wordOrder.length}
                wrongItems={wrongItems}
                onRetry={() => setSelectedCategory(null)}
            />
        );
    }

    // ── Quiz ─────────────────────────────────────────────────────────────────
    const word = words[wordOrder[currentWordIndex]];
    if (!word) return null;

    return (
        <>
            <QuizLayout
                current={currentWordIndex + 1}
                total={wordOrder.length}
                label={`Zelfstandige naamwoorden — ${selectedCategory}`}
                onBack={onBack}
            >
                <QuizCard
                    question={`Wat is ${word.dutch}?`}
                    accentWord={word.dutch}
                    questionIndex={currentWordIndex}
                    input={input}
                    onInputChange={setInput}
                    onSubmit={handleSubmit}
                    onNext={nextWord}
                    onShowAnswer={handleShowAnswer}
                    showAnswer={showAnswer}
                    answer={word.darija}
                />
            </QuizLayout>

            {messages.map((msg) => (
                <Snackbar
                    key={msg.id}
                    open
                    autoHideDuration={1500}
                    onClose={() => handleCloseMessage(msg.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => handleCloseMessage(msg.id)}
                        severity={msg.type}
                        sx={{ width: '100%', minWidth: 250 }}
                    >
                        {msg.text}
                    </Alert>
                </Snackbar>
            ))}
        </>
    );
}
